// Every editable string in the digital twin. The scene holds only the geometry bindings that decide
// WHERE a marker sits and WHICH materials it lights; everything a reader sees is here.
//
// Editing this file needs no knowledge of three.js. Change a title, body or spec row and the panel
// picks it up. The ids are the join to the scene, so renaming one means renaming its anchor in
// scene.ts too - TypeScript rejects an id that does not appear in both.

/** The four levels of the descent, shared by the content maps and the scene. */
export type View = 'hall' | 'rack' | 'tray' | 'gpu'

export type HotspotId =
  | 'hall.server-hall'
  | 'hall.hero-rack'
  | 'rack.footprint'
  | 'rack.power-shelves'
  | 'rack.management-switches'
  | 'rack.nvlink-switches'
  | 'rack.cooling-manifold'
  | 'rack.compute-trays'
  | 'tray.chassis'
  | 'tray.superchip-module'
  | 'tray.bluefield-dpu'
  | 'tray.supernics'
  | 'gpu.nvlink'
  | 'gpu.rubin-gpus'
  | 'gpu.vera-cpu'
  | 'gpu.pcie-midplane'

export type SpecRow = [label: string, value: string]

export type CompareRow = {
  label: string
  /** Bar lengths are relative to the larger of the two, so any consistent unit works. */
  rubin: number
  blackwell: number
  unit: string
}

export type HotspotCopy = {
  /** The pill beside the marker in the scene. Kept short; the panel carries the full title. */
  label: string
  title: string
  body: string
  specs?: SpecRow[]
  compare?: { note: string; rows: CompareRow[] }
}

/** Heading above the comparison bars, with each hotspot's own note appended. */
export const COMPARE_HEADING = 'Rubin vs Blackwell'

/** Shown while the scene builds. */
export const LOADING_TEXT = 'Assembling the rack…'

/** Shown in its place when the scene cannot start at all - no WebGL, or the chunk never arrived. */
export const UNAVAILABLE_TEXT =
  'The twin needs WebGL, and this browser will not start it. Everything it shows is in the recording.'

/** Footnote under every panel's figures. */
export const SPEC_NOTE =
  'Figures drawn from nvidia.com published Vera Rubin specifications and partner hardware documentation. Pre-release and subject to change.'

/** The four levels of the descent, as named in the rail. */
export const LEVEL_NAMES: Record<View, string> = {
  hall: 'The aisle',
  rack: 'Rack',
  tray: 'Compute tray',
  gpu: 'Superchip module',
}

// The rail's primary action. A 390px rail cannot hold the wide copy, so the narrow breakpoint (below
// the site's `md`) gets the same instruction said shorter rather than a wrapped button.
// The direction arrow is the shared ArrowRight icon, not a character - see TwinControlBar.
export const FORWARD_LABELS: Record<'desktop' | 'mobile', Record<View, string>> = {
  desktop: {
    hall: 'Approach the rack',
    rack: 'Pull out compute tray',
    tray: 'Open the superchip',
    gpu: 'Superchip module',
  },
  mobile: {
    hall: 'Approach rack',
    rack: 'Pull out tray',
    tray: 'Open module',
    gpu: 'Superchip module',
  },
}

// The page's sr-only H1 and meta description. See docs/unveiling.md#the-twins-copy.
export const PAGE_TITLE = 'Explore the NVIDIA Vera Rubin NVL72 in 3D'
export const PAGE_DESCRIPTION =
  "Explore NVIDIA's Vera Rubin NVL72 in 3D: open a compute tray, inspect the Vera Rubin Superchip and see how 72 Rubin GPUs fit in one rack. UK capacity from 2027."

// Where the forward control goes once there is no deeper level: the exit to the form. One label at
// every width, because the longer one wraps the rail at 768 once it clears the cookie icon.
export const EXIT_LABEL = 'Register interest'

/** Labels for the level stepper. Only levels already passed are reachable. */
export const STEP_LABELS: Record<View, { aria: string; title: string }> = {
  hall: { aria: 'Go back to the aisle', title: 'The aisle' },
  rack: { aria: 'Go back to the rack', title: 'Rack' },
  tray: { aria: 'Go back to the compute tray', title: 'Compute tray' },
  gpu: { aria: 'Superchip module', title: 'Superchip module' },
}

export const PANEL_CLOSE_LABEL = 'Close details'

// The rail's fixed controls. The primary action's label changes per level; see FORWARD_LABELS.
export const RAIL_LABELS = {
  back: 'Back',
  reset: { aria: 'Reset view', title: "Reset view - return to this step's default framing" },
  collapse: { show: 'Show controls', hide: 'Hide controls' },
  stepperGroup: 'Detail level',
} as const

export const HOTSPOT_COPY: Record<HotspotId, HotspotCopy> = {
  'hall.server-hall': {
    label: 'Server hall',
    title: 'Liquid-cooled server hall',
    body: 'Rows of racks line the aisle on a grated floor beneath overhead lighting; the highlighted Civo unit sits in the row until you approach it. Every rack here is fully liquid-cooled.',
    specs: [
      ['Cooling', '100% direct liquid'],
      ['Layout', 'Aisle-based hall'],
    ],
  },
  'hall.hero-rack': {
    label: 'Hero rack',
    title: 'Vera Rubin NVL72 rack',
    body: 'This is the rack we explore in full - approach it to begin pulling it apart, tray by tray, down to the Rubin GPU die.',
    specs: [
      ['Platform', 'NVIDIA Vera Rubin NVL72'],
      ['GPUs', '72 Rubin'],
      ['CPUs', '36 Vera'],
      ['Rack HBM4', '20.7 TB'],
    ],
  },
  'rack.footprint': {
    label: 'Rack footprint',
    title: 'Rack footprint',
    body: 'The cabinet keeps the same 600 mm width and 2.3 m height as the Blackwell generation and adds around 130 mm of depth, so it drops into a data hall built for the previous generation without re-engineering the room.',
    specs: [
      ['Depth (L)', '1,200 mm'],
      ['Width (W)', '600 mm'],
      ['Height (H)', '2,300 mm'],
      ['Footprint', 'Same 600 mm width and height as Blackwell'],
    ],
    compare: {
      note: 'width and height are unchanged generation over generation, by design',
      rows: [
        { label: 'Rack height', rubin: 2.3, blackwell: 2.3, unit: 'm' },
      ],
    },
  },
  'rack.power-shelves': {
    label: 'Power shelves',
    title: 'Power shelves',
    body: 'Power reaches the rack as high-voltage DC and these shelves step it down onto the bus bar, which runs the full height of the rack at the rear and feeds every tray. Each shelf holds six hot-swappable supplies behind perforated front panels, each with its own fan and a pair of status LEDs, so one can be swapped without dropping the shelf. A power management controller sits at the end of the row. The four shelves are split two at the top and two at the bottom, so losing either group still leaves the rack running.',
    specs: [
      ['Power shelves', '4 x 3RU, 2 top + 2 bottom'],
      ['Per shelf', '6 hot-swap 18.4 kW PSUs + controller'],
      ['Cooling', 'Air, fans behind the front grilles'],
      ['Rack feed', '800 VDC to a ~52V bus bar'],
    ],
  },
  'rack.management-switches': {
    label: 'Management switches',
    title: 'Out-of-band management switches',
    body: 'A separate, low-speed management network reaches every tray for monitoring and control, independent of the NVLink and InfiniBand/Ethernet data paths, so operators keep visibility even if a data-plane link is down.',
    specs: [
      ['Out-of-band switches', 'Top of rack'],
      ['Connection', 'RJ45 baseboard management (BMC)'],
    ],
  },
  'rack.nvlink-switches': {
    label: 'NVLink switch trays',
    title: 'NVLink switch trays',
    body: 'A dedicated bank of switch trays gives every GPU in the rack a high-bandwidth path to every other GPU, the fabric that lets 72 GPUs behave as one accelerator.',
    specs: [
      ['Switch trays', '9'],
      ['Rack NVLink bandwidth', '260 TB/s'],
    ],
  },
  'rack.cooling-manifold': {
    label: 'Liquid cooling manifold',
    title: 'Liquid cooling manifold',
    body: 'Two risers carry coolant to every liquid-cooled sledge: supply (cool, shown in green) down to the cold plates, return (warmed, shown in amber) back out. The risers run behind the tray bank and each drop turns forward into a rear face, so a tray couples simply by being pushed home - a blind-mate connection, since reaching into a 1U bay to attach a hose by hand is not practical. That also lets a tray be pulled for service without draining the loop. This is only the secondary loop: at the rack base both risers turn and run out to an in-row CDU, which passes the heat to the facility\'s primary loop through a liquid-to-liquid heat exchanger. At this power density the cooling load is beyond what an in-rack CDU can carry, so the heat exchange moves out into the row.',
    specs: [
      ['Cooling', '100% direct liquid, 45C warm water'],
      ['Loop', 'Secondary; liquid-to-liquid at the CDU'],
      ['In-row CDU', 'Shared across a row of racks, 2+1'],
      ['Cooled', '18 compute trays + 9 switch trays'],
      ['Servicing', 'Blind-mate quick-disconnect (UQD08)'],
    ],
  },
  'rack.compute-trays': {
    label: '18 compute trays',
    title: 'Compute tray bank',
    body: 'Eighteen liquid-cooled trays, each carrying a Vera Rubin superchip pair, and every one of them is real, clickable hardware, not just decoration: click any tray for a quick peek. One is marked out in aurora green, its handle and outline lit up, as the example you can pull fully out and explore down to the die level. Click that one, or the button below, to open it.',
    specs: [
      ['Trays', '18'],
      ['Form factor', '1U, liquid-cooled'],
    ],
  },
  'tray.chassis': {
    label: 'Compute tray',
    title: 'Serviceable compute tray',
    body: 'The tray glides forward on rails so a technician can pull, inspect, or swap a module without disturbing the rest of the rack, part of the resilience design behind the tier rating.',
    specs: [
      ['Form factor', '1U liquid-cooled node'],
    ],
  },
  'tray.superchip-module': {
    label: 'Vera Rubin superchip module',
    title: 'Vera Rubin superchip module',
    body: 'Two Vera Rubin superchips sit on this tray, each pairing one Vera CPU with two Rubin GPUs over NVLink-C2C. Click through to open it and see inside.',
    specs: [
      ['CPUs per tray', '2 x Vera'],
      ['GPUs per tray', '4 x Rubin'],
      ['GPU memory per tray', 'Up to 1,152 GB HBM4'],
      ['NVFP4 inference per tray', '200 PFLOPS'],
      ['Fast memory per tray', '4 TB'],
    ],
  },
  'tray.bluefield-dpu': {
    label: 'BlueField-4 DPU',
    title: 'BlueField-4 DPU',
    body: 'Sitting centre-stage on the tray, shared by both superchips, the BlueField-4 DPU takes on data-plane operations, hardware security isolation, and storage management, offloading all of it so the CPUs and GPUs stay focused purely on AI processing.',
    specs: [
      ['DPU modules per tray', '1'],
      ['Bandwidth', '800 Gb/s'],
      ['Ports', '2 x 400 Gb/s (dual-port)'],
      ['Handles', 'Data plane, security isolation, storage management'],
    ],
  },
  'tray.supernics': {
    label: 'ConnectX-9 SuperNICs',
    title: 'ConnectX-9 SuperNICs',
    body: 'Eight ConnectX-9 SuperNICs sit on the tray, two per Rubin GPU, bypassing the host CPU entirely to deliver scale-out network bandwidth straight off each GPU. This is the path that turns single racks into a connected, multi-site edge estate.',
    specs: [
      ['SuperNICs per tray', '8'],
      ['Scale-out bandwidth per GPU', '1.6 Tb/s'],
      ['Ports', '8 x 800 Gb/s OSFP'],
      ['Fabric options', 'Quantum-X800 InfiniBand or Spectrum-X Ethernet'],
    ],
    compare: {
      note: 'ConnectX-9 pairs two 800G NICs per GPU, doubling scale-out against Blackwell NVL72',
      rows: [
        { label: 'Per-GPU scale-out', rubin: 1.6, blackwell: 0.8, unit: 'Tb/s' },
      ],
    },
  },
  'gpu.nvlink': {
    label: 'NVLink 6',
    title: 'NVLink 6 spine connectors',
    body: 'Two cable-free, blind-mate connectors along this superchip’s rear edge carry its NVLink 6 traffic out to the rack’s switch trays. No cables to route or unplug - the module makes its fabric connection simply by seating.',
    specs: [
      ['Connectors per superchip', '2'],
      ['GPU-to-GPU bandwidth', '3.6 TB/s per GPU'],
    ],
  },
  'gpu.rubin-gpus': {
    label: 'Rubin GPUs',
    title: 'Rubin GPUs',
    body: 'Two Rubin GPUs sit side by side at the top of the board, the largest packages on the module and the reason the rack, the cooling and the networking all exist. HBM4 memory sits on the package itself, inside the gold frame, rather than as separate parts on the board.',
    specs: [
      ['GPUs per superchip', '2'],
      ['HBM4 per GPU', '288 GB'],
      ['NVFP4 inference per GPU', '50 PFLOPS'],
      ['NVFP4 inference per superchip', '100 PFLOPS'],
      ['Fast memory per superchip', '2 TB'],
      ['NVLink 6 bandwidth', 'Up to 3.6 TB/s per GPU'],
    ],
  },
  'gpu.vera-cpu': {
    label: 'Vera CPU',
    title: 'Vera CPU',
    body: 'One Vera CPU per superchip, centred below the GPU pair. Custom Arm-based cores that feed both GPUs and handle orchestration, connected over NVLink-C2C directly rather than a standard bus.',
    specs: [
      ['CPUs per superchip', '1'],
      ['Cores per CPU', '88 (Olympus)'],
      ['Memory per CPU', 'Up to 1.5 TB LPDDR5X'],
    ],
  },
  'gpu.pcie-midplane': {
    label: 'PCIe Gen6 Midplane',
    title: 'PCIe Gen6 midplane connectors',
    body: 'Three gold connector segments run along this superchip’s front edge, carrying power, PCIe Gen6 and CXL. They are how the module seats into the tray’s midplane, giving it a path to the BlueField-4 DPU and the ConnectX-9 SuperNICs sitting further forward on the tray.',
    specs: [
      ['Connectors per superchip', '3 (power, PCIe, CXL)'],
      ['Interface', 'PCIe Gen6'],
    ],
  },
}
